import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { extractErrorMessage } from '@ots-frontend/ots-data';
import { AdminPanel } from './AdminPanel';
import { OtsActionButton, OtsActionLink } from './OtsButton';
import { OtsInlineAlert } from './OtsInlineAlert';
import { OtsDataTableEmpty } from './OtsDataTableCells';

const PdfCanvas = lazy(() => import('./OtsPdfCanvas'));

export type OtsPdfViewerProps = {
  documentKey: string;
  title: string;
  fileName: string;
  loadPdf: (signal: AbortSignal) => Promise<Blob>;
  compact?: boolean;
  initiallyOpen?: boolean;
};
const Viewer = ({
  title,
  fileName,
  loadPdf,
  compact,
  initiallyOpen,
}: OtsPdfViewerProps) => {
  const [open, setOpen] = useState(initiallyOpen ?? false);
  const [revision, refresh] = useState(0);
  const [pdf, setPdf] = useState<Blob>();
  const [url, setUrl] = useState<string>();
  const [error, setError] = useState<string>();
  const loader = useRef(loadPdf);
  loader.current = loadPdf;
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    let objectUrl: string | undefined;
    setUrl(undefined);
    setPdf(undefined);
    setError(undefined);
    void Promise.resolve()
      .then(() =>
        controller.signal.aborted
          ? undefined
          : loader.current(controller.signal)
      )
      .then((blob) => {
        if (!controller.signal.aborted && blob) {
          setPdf(blob);
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }
      })
      .catch((reason) => {
        if (!controller.signal.aborted)
          setError(extractErrorMessage(reason, 'The PDF could not be loaded.'));
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, revision]);
  return (
    <AdminPanel
      title={title}
      compact={compact}
      bodyClassName="p-4"
      actions={
        <div className="flex flex-wrap gap-2">
          {open && (
            <OtsActionButton
              size="compact"
              onClick={() => refresh((value) => value + 1)}
            >
              Refresh PDF
            </OtsActionButton>
          )}
          <OtsActionButton
            size="compact"
            onClick={() => {
              setOpen(!open);
              setUrl(undefined);
              setPdf(undefined);
              setError(undefined);
            }}
          >
            {open ? 'Hide PDF' : 'Preview PDF'}
          </OtsActionButton>
          {url && (
            <>
              <OtsActionLink
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                size="compact"
              >
                Open PDF in new tab
              </OtsActionLink>
              <OtsActionLink href={url} download={fileName} size="compact">
                Download PDF
              </OtsActionLink>
            </>
          )}
        </div>
      }
    >
      {!open ? (
        <p className="text-sm text-[var(--ots-text-muted)]">
          Preview the selected version without leaving this view.
        </p>
      ) : error ? (
        <OtsInlineAlert
          tone="danger"
          title="PDF unavailable"
          action={
            <OtsActionButton
              size="compact"
              onClick={() => refresh((value) => value + 1)}
            >
              Try again
            </OtsActionButton>
          }
        >
          {error}
        </OtsInlineAlert>
      ) : !url ? (
        <OtsDataTableEmpty loading loadingText="Loading PDF…">
          {null}
        </OtsDataTableEmpty>
      ) : (
        <>
          <p className="mb-3 text-sm text-[var(--ots-text-muted)]">
            You can also open the PDF in a new tab or download it.
          </p>
          {pdf && (
            <Suspense
              fallback={
                <OtsDataTableEmpty loading loadingText="Preparing PDF preview…">
                  {null}
                </OtsDataTableEmpty>
              }
            >
              <PdfCanvas blob={pdf} title={title} />
            </Suspense>
          )}
        </>
      )}
    </AdminPanel>
  );
};
/** Identity changes abort pending work, discard the previous document, and revoke its browser URL. */
export const OtsPdfViewer = (props: OtsPdfViewerProps) => (
  <Viewer key={props.documentKey} {...props} />
);
